import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/motion/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";

const TEAM_IMG =
  "https://static.prod-images.emergentagent.com/jobs/731c2359-9c2f-41d4-b55e-9ac22f27bfd0/images/064564039ca2a6ebb3d9e18a8e7c2aefc38df91b2408fd179068b4d7fc70c9a2.jpeg";

function PageHero({ eyebrow, title, sub }) {
  return (
    <section className="relative pt-40 pb-16 lg:pb-24 overflow-hidden">
      <div
        className="absolute -top-20 right-0 w-[600px] h-[600px] pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.10), transparent 60%)" }}
      />
      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow={eyebrow} title={title} sub={sub} />
      </div>
    </section>
  );
}

export default function About() {
  const { t } = useLang();
  const a = t.about;

  return (
    <div data-testid="about-page">
      <PageHero eyebrow={a.heroEyebrow} title={a.heroTitle} sub={a.heroSub} />

      {/* Vision / Mission with image */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
          <div className="lg:col-span-6 flex flex-col gap-6">
            <Reveal>
              <div className="border border-white/10 bg-navy-surface/40 p-8 lg:p-10">
                <span className="font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision">
                  {a.visionTitle}
                </span>
                <p className="mt-5 text-xl text-[#F0F4F8] font-display font-light leading-snug">
                  {a.visionBody}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-white/10 bg-navy-surface/40 p-8 lg:p-10">
                <span className="font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision">
                  {a.missionTitle}
                </span>
                <p className="mt-5 text-xl text-[#F0F4F8] font-display font-light leading-snug">
                  {a.missionBody}
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.15}>
              <div
                className="relative overflow-hidden border border-white/10 h-full min-h-[420px]"
                style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 12%)" }}
              >
                <img src={TEAM_IMG} alt="Executive team" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Methodology manifesto */}
      <section data-testid="methodology" className="py-28 lg:py-36 border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={a.methodEyebrow} title={a.methodTitle} />
          <div className="mt-16 space-y-0">
            {a.method.map((m, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="group grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-t border-white/10 last:border-b transition-colors hover:bg-white/[0.015]">
                  <div className="md:col-span-3">
                    <span className="font-mono-tech text-6xl lg:text-7xl font-bold text-[#F0F4F8]/10 group-hover:text-cyan-precision/40 transition-colors">
                      {m.n}
                    </span>
                  </div>
                  <div className="md:col-span-4">
                    <h3 className="font-display text-3xl font-light tracking-tight text-[#F0F4F8]">
                      {m.title}
                    </h3>
                  </div>
                  <div className="md:col-span-5">
                    <p className="text-[#94A3B8] leading-relaxed text-lg">{m.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-28 lg:py-36 border-t border-white/10">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          <SectionHeading title={a.valuesTitle} />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {a.values.map((v, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full border border-white/10 bg-navy-surface/40 p-8 hover:border-cyan-precision/50 hover:-translate-y-1 transition-all duration-300">
                  <div className="font-mono-tech text-cyan-precision/70 text-xs">0{i + 1}</div>
                  <h4 className="mt-4 font-display text-xl text-[#F0F4F8]">{v.t}</h4>
                  <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{v.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection title={t.home.ctaTitle} body={t.home.ctaBody} btn={t.home.ctaBtn} />
    </div>
  );
}
